// client/src/components/settings/BusinessProfileSettings.js
// Edit business identity, logo, and example posts (style references)
// Uses saveProfile (upsert) so it works for both new and existing records.

import React, { useState, useEffect, useRef } from 'react';
import { businessAPI } from '../../services/businessAPI';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BUSINESS_TYPES = [
  'Cafe / Coffee Shop', 'Restaurant / Food Service', 'Retail Store',
  'Creative Studio / Art Gallery', 'Fitness / Wellness / Gym',
  'Professional Services / Consulting', 'Education / Training / Coaching',
  'Entertainment / Events', 'Beauty / Salon / Spa', 'Healthcare / Medical',
  'Technology / Software', 'Real Estate / Property',
  'Marketing / Advertising Agency', 'Photography / Videography',
  'Home Services / Trades', 'Other'
];

const TEAM_SIZES = [
  'Just me', '2-5 people', '6-10 people', '11-25 people', '26-50 people', '50+ people'
];

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${API_URL}${url}`;
};

// Old /uploads/ paths saved before the base64 fix are dead on Railway (ephemeral filesystem)
const isDeadPath = (url) => url && !url.startsWith('data:') && !url.startsWith('http');

export default function BusinessProfileSettings({ businessData, onUpdate }) {
  // ── Basic fields ─────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    businessName:   '',
    businessType:   '',
    location:       '',
    phone:          '',
    email:          '',
    website:        '',
    foundedYear:    '',
    teamSize:       '',
    operatingHours: '',
    description:    ''
  });

  // ── Brand DNA (logo + example posts) ─────────────────────────────────
  const [logoUrl,        setLogoUrl]        = useState('');
  const [styleExamples,  setStyleExamples]  = useState(['', '', '']);

  // ── Upload state ──────────────────────────────────────────────────────
  const [uploadingLogo,  setUploadingLogo]  = useState(false);
  const [uploadingStyle, setUploadingStyle] = useState(-1); // index or -1
  const logoInputRef  = useRef(null);
  const styleRefs     = [useRef(null), useRef(null), useRef(null)];

  // ── UI state ──────────────────────────────────────────────────────────
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');

  // ── Populate from existing data ───────────────────────────────────────
  useEffect(() => {
    if (!businessData) return;
    setFormData({
      businessName:   businessData.businessName   || '',
      businessType:   businessData.businessType   || '',
      location:       businessData.location       || '',
      phone:          businessData.phone          || '',
      email:          businessData.email          || '',
      website:        businessData.website        || '',
      foundedYear:    businessData.foundedYear    || '',
      teamSize:       businessData.teamSize       || '',
      operatingHours: businessData.operatingHours || '',
      description:    businessData.description    || ''
    });
    const dna = businessData.brandDna || {};
    setLogoUrl(dna.logoUrl || '');
    setStyleExamples(
      Array.isArray(dna.styleExamples) && dna.styleExamples.length === 3
        ? dna.styleExamples
        : ['', '', '']
    );
  }, [businessData]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const setField = (k, v) => { setFormData(p => ({ ...p, [k]: v })); setSuccess(''); setError(''); };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    setError('');
    try {
      const res = await businessAPI.uploadLogo(file);
      if (res.success) setLogoUrl(res.logoUrl);
    } catch {
      setError('Logo upload failed. Please try again.');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleStyleUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingStyle(index);
    setError('');
    try {
      const res = await businessAPI.uploadStyleExample(file);
      if (res.success) {
        setStyleExamples(prev => {
          const next = [...prev];
          next[index] = res.imageUrl;
          return next;
        });
      }
    } catch {
      setError('Image upload failed. Please try again.');
    } finally {
      setUploadingStyle(-1);
      if (styleRefs[index].current) styleRefs[index].current.value = '';
    }
  };

  const removeStyle = (index) => {
    setStyleExamples(prev => { const next = [...prev]; next[index] = ''; return next; });
  };

  const handleSave = async () => {
    if (!formData.businessName.trim()) { setError('Business name is required.'); return; }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Merge new brandDna values with any existing brandDna fields we don't edit here
      const existingDna = businessData?.brandDna || {};
      const mergedDna   = { ...existingDna, logoUrl, styleExamples };

      const res = await businessAPI.saveProfile({ ...formData, brandDna: mergedDna });
      if (res.success) {
        setSuccess('Business profile saved successfully.');
        setTimeout(() => setSuccess(''), 3000);
        if (onUpdate) onUpdate(res.profile);
      } else {
        setError('Failed to save. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  const inp = 'w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
  const lbl = 'block text-[11px] font-semibold text-slate-600 mb-1';

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Feedback banners */}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <i className="fas fa-check-circle text-green-600 text-sm"></i>
          <p className="text-[12px] text-green-700 font-medium">{success}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <i className="fas fa-exclamation-circle text-red-500 text-sm"></i>
          <p className="text-[12px] text-red-700 font-medium">{error}</p>
        </div>
      )}

      {/* ── Section 1: Business Identity ──────────────────────────────── */}
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5 bg-slate-50">
          <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
            <i className="fas fa-building text-white text-xs"></i>
          </div>
          <h2 className="text-[13px] font-bold text-slate-800">Business Identity</h2>
          <span className="text-[10px] text-slate-400 ml-auto">Core details used across all AI content</span>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Business Name */}
          <div>
            <label className={lbl}>Business Name <span className="text-red-500">*</span></label>
            <input type="text" value={formData.businessName}
              onChange={e => setField('businessName', e.target.value)}
              placeholder="e.g. The Coffee Corner"
              className={inp} />
          </div>

          {/* Business Type */}
          <div>
            <label className={lbl}>Business Type</label>
            <select value={formData.businessType} onChange={e => setField('businessType', e.target.value)} className={inp}>
              <option value="">Select type…</option>
              {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Location */}
          <div>
            <label className={lbl}>Location</label>
            <input type="text" value={formData.location}
              onChange={e => setField('location', e.target.value)}
              placeholder="e.g. Manchester, UK" className={inp} />
          </div>

          {/* Phone */}
          <div>
            <label className={lbl}>Phone Number</label>
            <input type="tel" value={formData.phone}
              onChange={e => setField('phone', e.target.value)}
              placeholder="+44 20 1234 5678" className={inp} />
          </div>

          {/* Email */}
          <div>
            <label className={lbl}>Business Email</label>
            <input type="email" value={formData.email}
              onChange={e => setField('email', e.target.value)}
              placeholder="hello@yourbusiness.com" className={inp} />
          </div>

          {/* Website */}
          <div>
            <label className={lbl}>Website</label>
            <input type="url" value={formData.website}
              onChange={e => setField('website', e.target.value)}
              placeholder="https://yourbusiness.com" className={inp} />
          </div>

          {/* Founded Year */}
          <div>
            <label className={lbl}>Founded Year</label>
            <input type="number" value={formData.foundedYear}
              onChange={e => setField('foundedYear', e.target.value)}
              placeholder="2020" min="1900" max={new Date().getFullYear()} className={inp} />
          </div>

          {/* Team Size */}
          <div>
            <label className={lbl}>Team Size</label>
            <select value={formData.teamSize} onChange={e => setField('teamSize', e.target.value)} className={inp}>
              <option value="">Select size…</option>
              {TEAM_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Operating Hours — full width */}
          <div className="md:col-span-2">
            <label className={lbl}>Operating Hours</label>
            <input type="text" value={formData.operatingHours}
              onChange={e => setField('operatingHours', e.target.value)}
              placeholder="e.g. Mon–Fri 9am–6pm, Sat 10am–4pm" className={inp} />
          </div>

          {/* Description — full width */}
          <div className="md:col-span-2">
            <label className={lbl}>Business Description</label>
            <textarea value={formData.description}
              onChange={e => setField('description', e.target.value)}
              rows={3} placeholder="What your business does and what makes it unique…"
              className={`${inp} resize-none`} />
          </div>
        </div>
      </div>

      {/* ── Section 2: Visual Brand ────────────────────────────────────── */}
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5 bg-slate-50">
          <div className="w-7 h-7 rounded-lg bg-purple-600 flex items-center justify-center">
            <i className="fas fa-palette text-white text-xs"></i>
          </div>
          <h2 className="text-[13px] font-bold text-slate-800">Visual Brand</h2>
          <span className="text-[10px] text-slate-400 ml-auto">Your logo and brand visuals for AI-generated content</span>
        </div>

        <div className="p-5">
          {/* Logo upload */}
          <div className="mb-5">
            <label className={lbl}>Business Logo <span className="text-slate-400 font-normal">(PNG preferred)</span></label>
            <div className="flex items-center gap-4">
              {/* Preview */}
              <div className={`w-20 h-20 rounded-xl border-2 flex items-center justify-center overflow-hidden shrink-0 ${
                logoUrl ? (isDeadPath(logoUrl) ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50') : 'border-dashed border-slate-300 bg-slate-50'
              }`}>
                {logoUrl
                  ? isDeadPath(logoUrl)
                    ? <div className="flex flex-col items-center gap-1 p-2"><i className="fas fa-exclamation-triangle text-red-400 text-lg"></i><span className="text-[8px] text-red-500 font-bold text-center leading-tight">Re-upload needed</span></div>
                    : <img src={getFullUrl(logoUrl)} alt="Logo" className="max-w-full max-h-full object-contain p-1" />
                  : <i className="fas fa-image text-slate-300 text-2xl"></i>
                }
              </div>

              {/* Controls */}
              <div>
                <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                <button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="px-4 py-2 text-[12px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                >
                  {uploadingLogo
                    ? <><i className="fas fa-spinner fa-spin mr-1.5"></i>Uploading…</>
                    : <><i className="fas fa-upload mr-1.5"></i>{logoUrl ? 'Replace Logo' : 'Upload Logo'}</>
                  }
                </button>
                {logoUrl && (
                  <>
                    <p className="text-[11px] text-green-600 mt-1.5 font-medium">
                      <i className="fas fa-check-circle mr-1"></i>Logo uploaded
                    </p>
                    <button onClick={() => setLogoUrl('')}
                      className="text-[11px] text-red-400 hover:text-red-600 mt-0.5">
                      <i className="fas fa-trash mr-1"></i>Remove
                    </button>
                  </>
                )}
                <p className="text-[10px] text-slate-400 mt-1">Used in Design Studio and AI-generated posts</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 3: Example Posts You Love ────────────────────────── */}
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5 bg-slate-50">
          <div className="w-7 h-7 rounded-lg bg-green-600 flex items-center justify-center">
            <i className="fas fa-images text-white text-xs"></i>
          </div>
          <h2 className="text-[13px] font-bold text-slate-800">Example Posts You Love</h2>
          <span className="text-[10px] text-slate-400 ml-auto">Upload up to 3 images</span>
        </div>

        <div className="p-5">
          <p className="text-[12px] text-slate-500 mb-4">
            Upload 1–3 example posts from competitors or brands you admire. Our AI will learn from these.
          </p>

          <div className="grid grid-cols-3 gap-4">
            {[0, 1, 2].map(i => (
              <div key={i}>
                <input
                  ref={styleRefs[i]}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={e => handleStyleUpload(i, e)}
                  className="hidden"
                />

                {styleExamples[i] ? (
                  /* Image uploaded — show preview with hover controls */
                  <div className={`relative group aspect-square rounded-xl border-2 overflow-hidden ${
                    isDeadPath(styleExamples[i]) ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
                  }`}>
                    {isDeadPath(styleExamples[i]) ? (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 p-3">
                        <i className="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
                        <span className="text-[9px] text-red-600 font-bold text-center leading-tight">Re-upload needed</span>
                        <span className="text-[8px] text-red-400 text-center">Click to re-upload</span>
                      </div>
                    ) : (
                      <img
                        src={getFullUrl(styleExamples[i])}
                        alt={`Example ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                      <button
                        onClick={() => styleRefs[i].current?.click()}
                        className="p-2 bg-white rounded-full text-slate-700 hover:text-blue-600 transition-colors"
                        title="Replace"
                      >
                        <i className="fas fa-sync-alt text-xs"></i>
                      </button>
                      <button
                        onClick={() => removeStyle(i)}
                        className="p-2 bg-white rounded-full text-slate-700 hover:text-red-600 transition-colors"
                        title="Remove"
                      >
                        <i className="fas fa-trash text-xs"></i>
                      </button>
                    </div>
                    {/* Status badge */}
                    <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center shadow ${isDeadPath(styleExamples[i]) ? 'bg-red-400' : 'bg-green-500'}`}>
                      <i className={`text-white text-[8px] ${isDeadPath(styleExamples[i]) ? 'fas fa-exclamation' : 'fas fa-check'}`}></i>
                    </div>
                  </div>
                ) : (
                  /* Empty slot */
                  <button
                    onClick={() => styleRefs[i].current?.click()}
                    disabled={uploadingStyle === i}
                    className="w-full aspect-square rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50 transition-all flex flex-col items-center justify-center gap-1.5"
                  >
                    {uploadingStyle === i ? (
                      <i className="fas fa-spinner fa-spin text-blue-500 text-xl"></i>
                    ) : (
                      <>
                        <i className="fas fa-cloud-upload-alt text-slate-300 text-xl"></i>
                        <span className="text-[11px] text-slate-400 font-medium">Image {i + 1}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>

          {styleExamples.filter(Boolean).length > 0 && (
            <p className="text-[11px] text-green-600 mt-3 font-medium">
              <i className="fas fa-check-circle mr-1"></i>
              {styleExamples.filter(Boolean).length} image{styleExamples.filter(Boolean).length > 1 ? 's' : ''} uploaded
            </p>
          )}
        </div>
      </div>

      {/* ── Save button ──────────────────────────────────────────────── */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={saving || !formData.businessName.trim()}
          className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-[13px] font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-sm"
        >
          {saving
            ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Saving…</>
            : <><i className="fas fa-save"></i>Save Business Profile</>
          }
        </button>
      </div>
    </div>
  );
}
